import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { getApiUrl } from '../../config/api';
import { getAuth as realGetAuth } from 'firebase/auth';
import type { TrackPermissions } from '../../types/track';

interface NotesSectionProps {
  trackId: string;
  initialNotes?: string;
  getAuth?: typeof realGetAuth;
  permissions?: TrackPermissions;
}

const NotesSection: React.FC<NotesSectionProps> = ({ trackId, initialNotes = '', getAuth = realGetAuth, permissions }) => {
  const { t } = useTranslation();
  const [note, setNote] = useState(initialNotes);
  const [status, setStatus] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');
  const saveTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    setNote(initialNotes);
    setDirty(false);
  }, [trackId, initialNotes]);

  useEffect(() => {
    if (!dirty) return;
    if (saveTimeout.current) clearTimeout(saveTimeout.current);
    if (note === '') {
      setStatus('idle');
      return;
    }
    setStatus('saving');
    saveTimeout.current = setTimeout(async () => {
      try {
        const user = getAuth().currentUser;
        if (!user) throw new Error('No user');
        const token = await user.getIdToken();
        const res = await fetch(getApiUrl(`/tracks/${trackId}/notes`), {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ notes: note }),
        });
        if (!res.ok) throw new Error('Failed to save notes');
        setStatus('saved');
        setDirty(false);
        setTimeout(() => setStatus('idle'), 1200);
      } catch {
        setStatus('error');
      }
    }, 1000);
    return () => {
      if (saveTimeout.current) clearTimeout(saveTimeout.current);
    };
  }, [note, trackId, dirty, getAuth]);

  return (
    <div>
      <textarea
        id="track-note"
        className="w-full rounded p-2 mb-2 min-h-[80px] focus:outline-none focus:border-[var(--brand-from)] border"
        style={{ backgroundColor: 'var(--color-surface)', color: 'var(--color-text)', borderColor: 'var(--color-border)' }}
        placeholder={permissions?.canEditMetadata ? t('notes.context') : 'No notes available'}
        value={note}
        onChange={permissions?.canEditMetadata ? (e => { setNote(e.target.value); setDirty(true); }) : undefined}
        readOnly={!permissions?.canEditMetadata}
        data-testid="notes-textarea"
      />
      <div className="text-xs h-5" style={{ color: 'var(--color-muted)' }}>
        {status === 'saving' && t('notes.saving')}
        {status === 'saved' && t('notes.saved')}
        {status === 'error' && t('notes.error')}
      </div>
    </div>
  );
};

export default NotesSection; 